import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HiArrowLongLeft, HiArrowLongRight } from "react-icons/hi2";
import TrendingSection from "../components/TrendingSection";
import ScrollStackSection from "../components/ScrollStackSection";
import ScrollSeparationModel from "../components/ScrollSeparationModel";

gsap.registerPlugin(ScrollTrigger);

const impactCardsData = [
    {
        title: "A1C Reduction",
        description:
            "Up to 2.4 percentage points in clinical trials, Mounjaro reduced A1C levels by up to 2.4%, which is significantly more than many other diabetes medications.",
    },
    {
        title: "Weight Loss",
        description:
            "In clinical trials, participants lost up to 22.5% of their body weight (up to 52 lbs) when taking Mounjaro combined with diet and exercise.",
    },
    {
        title: "Dosing Frequency",
        description:
            "Once per week (every 7 days) Mounjaro is administered as a once-weekly injection, which improves adherence for many users.",
    },
    {
        title: "Doses Available",
        description:
            "6 dose strengths Mounjaro comes in 2.5, 5, 7.5, 10, 12.5, and 15 mg doses, allowing for a gradual increase to minimize side effects and maximize tolerance.",
    },
];

const ImpactCard = ({ title, description }) => {
    const cardRef = useRef(null);
    const rotationRef = useRef({ x: 0, y: 0 });
    const rectRef = useRef(null);
    const rafRef = useRef(null);
    const isHoveringRef = useRef(false);

    const updateTransform = () => {
        if (!cardRef.current || !isHoveringRef.current) return;
        
        const { x, y } = rotationRef.current;
        if (cardRef.current) {
            cardRef.current.style.transform = `perspective(1000px) rotateX(${x}deg) rotateY(${y}deg) scale3d(1, 1, 1)`;
        }
        rafRef.current = null;
    };

    const handleMouseMove = (e) => {
        const card = cardRef.current;
        if (!card) return;

        if (!rectRef.current) {
            rectRef.current = card.getBoundingClientRect();
        }

        const { clientX, clientY } = e;
        const { left, top, width, height } = rectRef.current;
        const x = clientX - left;
        const y = clientY - top;
        const rotateX = (y / height - 0.5) * -20;
        const rotateY = (x / width - 0.5) * 20;

        rotationRef.current = { x: rotateX, y: rotateY };

        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(updateTransform);
        }
    };

    const handleMouseLeave = () => {
        isHoveringRef.current = false;
        rotationRef.current = { x: 0, y: 0 };
        rectRef.current = null;
        
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        
        if (cardRef.current) {
            cardRef.current.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";
        }
    };

    const handleMouseEnter = () => {
        isHoveringRef.current = true;
        if (cardRef.current) {
            rectRef.current = cardRef.current.getBoundingClientRect();
        }
    };

    return (
        <div
            ref={cardRef}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="group bg-[#a7b4c2] p-6 rounded-lg text-black flex flex-col justify-between min-h-[300px] md:h-[500px] text-left"
            style={{
                transition: "transform 0.4s ease-out",
            }}
        >
            <div>
                <h4
                    className="text-2xl md:text-4xl w-full md:w-1/2 font-bold mb-4 transition-transform duration-300 ease-in-out group-hover:scale-110"
                    style={{ transform: "translateZ(50px)" }}
                >
                    {title}
                </h4>
            </div>
            <p className="text-md">{description}</p>
        </div>
    );
};

const injectionCards = [
    {
        id: 1,
        title: "Choose Injection Site",
        description:
            "You can inject Mounjaro in your stomach (abdomen), thigh, or upper arm. If you want to use your upper arm, ask someone else to help you with the injection.",
        image: "/images/benifits/placeholder-1.png",
        imageAlt: "Injection Site Selection",
        backgroundColor: "#252C34",
    },
    {
        id: 2,
        title: "Prepare the Pen",
        description:
            "Wash your hands. Pull the gray base cap straight off. Do not touch the needle. Make sure the pen is locked until you are ready to inject.",
        image: "/images/benifits/placeholder-2.png",
        imageAlt: "Pen Preparation",
        backgroundColor: "#323B46",
    },
    {
        id: 3,
        title: "Injecting Mounjaro",
        description:
            "Place the clear base flat against your skin at the injection site. Unlock the pen. Press and hold the purple injection button for up to 10 seconds. Listen for the click.",
        image: "/images/benifits/placeholder-3.png",
        imageAlt: "Injection Process",
        backgroundColor: "#64768C",
    },
    {
        id: 4,
        title: "Weekly Rotation",
        description:
            "Rotate your injection site with each weekly dose. You may inject in the same area, but try not to inject in the exact same spot as the week before.",
        image: "/images/benifits/placeholder-4.png",
        imageAlt: "Site Rotation",
        backgroundColor: "#A4B4C7",
    },
];

const testimonials = [
    {
        name: "Alex",
        title: "Results may vary",
        image: "https://randomuser.me/api/portraits/women/68.jpg",
        text: "I am based in the UK. I first came across Mounjaro in November 2024 I was 99KG's at that time. I started my journey on a 2.5mg for the first month then 5mg, then 7.5mg now I am on 10mg my plan is is to begin reducing the dosage. My weight currently is 79.3KG's my height is 170cm, so my goal is to reach around 73KG's. I feel much better than I use to, fluid in my legs has gone, fat belly gone, I have almost reduced alcohol intake to zero from twenty units per week.",
    },
    {
        name: "Michael R.",
        title: "Software Engineer",
        image: "https://randomuser.me/api/portraits/men/75.jpg",
        text: "This has been a life-changer. My energy levels are up and I've finally been able to lose the weight I've struggled with for years. I'm down 30lbs in 4 months.",
    },
    {
        name: "Sarah L.",
        title: "Graphic Designer",
        image: "https://randomuser.me/api/portraits/women/75.jpg",
        text: "I was skeptical, but the results speak for themselves. The once-a-week injection is convenient. My overall health has improved dramatically.",
    },
    {
        name: "Emily W.",
        title: "Project Manager",
        image: "https://randomuser.me/api/portraits/women/76.jpg",
        text: "Significant changes in just a few months. It's not just about the weight loss, but feeling healthier. I feel in control of my appetite again.",
    },
    {
        name: "David B.",
        title: "Accountant",
        image: "https://randomuser.me/api/portraits/men/76.jpg",
        text: "My doctor recommended Mounjaro and it's been the best decision. Simple process, minimal side effects for me. I've lost over 15% of my body weight.",
    },
];

function TestimonialCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const testimonialRef = useRef(null);
    const imageRef = useRef(null);

    const handleNext = () => {
        const nextIndex = (currentIndex + 1) % testimonials.length;
        animateTo(nextIndex);
    };

    const handlePrev = () => {
        const prevIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
        animateTo(prevIndex);
    };

    const animateTo = (index) => {
        if (index === currentIndex) return;
        const direction = index > currentIndex ? 1 : -1;

        gsap
            .timeline()
            .to(testimonialRef.current, {
                opacity: 0,
                x: -50 * direction,
                duration: 0.4,
                ease: "power2.inOut",
            })
            .to(
                imageRef.current,
                {
                    opacity: 0,
                    scale: 0.9,
                    duration: 0.4,
                    ease: "power2.inOut",
                },
                "-=0.4"
            )
            .call(() => setCurrentIndex(index))
            .set(testimonialRef.current, { x: 50 * direction })
            .set(imageRef.current, { scale: 1.1 })
            .to(testimonialRef.current, {
                opacity: 1,
                x: 0,
                duration: 0.4,
                ease: "power2.inOut",
            })
            .to(
                imageRef.current,
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.inOut",
                },
                "-=0.4"
            );
    };

    const currentTestimonial = testimonials[currentIndex];

    return (
        <section className="bg-gray-100 px-6 md:px-20 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16 items-center">
                <div className="lg:hidden order-1 text-left">
                    <div ref={testimonialRef}>
                        <p className="text-lg leading-relaxed">"{currentTestimonial.text}"</p>
                    </div>
                </div>

                <div className="lg:col-span-1 flex flex-col items-start text-left order-2 lg:order-1">
                    <div className="w-full">
                        <div className="flex items-center justify-between space-x-4">
                            <h3 className="font-bold">Testimonials</h3>
                            <span>
                0{currentIndex + 1} / 0{testimonials.length}
              </span>
                        </div>
                        <div className="mt-2 h-px bg-gray-300 w-full"></div>
                    </div>

                    <div ref={imageRef} className="mt-8 text-center w-full flex flex-col items-center">
                        <img
                            src={currentTestimonial.image}
                            alt={currentTestimonial.name}
                            className="w-24 h-24 rounded-full object-cover"
                        />
                        <p className="mt-4 font-bold">{currentTestimonial.name}</p>
                        <p className="text-gray-500 text-sm">{currentTestimonial.title}</p>
                    </div>

                    <div className="mt-8 flex space-x-4 justify-center w-full">
                        <button
                            onClick={handlePrev}
                            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-white"
                        >
                            <HiArrowLongLeft className="h-6 w-6" />
                        </button>
                        <button
                            onClick={handleNext}
                            className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-white"
                        >
                            <HiArrowLongRight className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <div className="hidden lg:block lg:col-span-3 text-left order-3 lg:order-2">
                    <div ref={testimonialRef}>
                        <p className="text-lg leading-relaxed">"{currentTestimonial.text}"</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Benefits() {
    const modelSectionRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const sections = gsap.utils.toArray(".animated-section");
            sections.forEach((section) => {
                const elementsToAnimate = gsap.utils.toArray(section.children);
                gsap.from(elementsToAnimate, {
                    y: 50,
                    opacity: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none",
                    },
                });
            });
        });
        return () => ctx.revert();
    }, []);

    useEffect(() => {
        const section = modelSectionRef.current;
        if (!section) return;

        let lastScrollY = window.scrollY;
        let isInsideSection = false;

        const triggerAnimation = (direction = "forward") => {
            window.dispatchEvent(
                new CustomEvent("triggerModelAnimation", {
                    detail: { direction },
                })
            );
        };

        const handleScroll = () => {
            const rect = section.getBoundingClientRect();

            const inView =
                rect.top < window.innerHeight * 0.6 &&
                rect.bottom > window.innerHeight * 0.4;

            const currentY = window.scrollY;
            const scrollingDown = currentY > lastScrollY;
            const scrollingUp = currentY < lastScrollY;

            if (inView && !isInsideSection) {
                isInsideSection = true;

                if (scrollingDown) {
                    triggerAnimation("forward");
                } else if (scrollingUp) {
                    triggerAnimation("reverse");
                }
            }

            if (inView && isInsideSection) {
                if (scrollingDown) {
                    triggerAnimation("forward");
                } else if (scrollingUp) {
                    triggerAnimation("reverse");
                }
            }

            if (!inView && isInsideSection) {
                isInsideSection = false;
            }

            lastScrollY = currentY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <div className="min-h-screen bg-white">
            {/* Header Section */}
            <section className="animated-section bg-gray-100 px-6 md:px-20 py-12 md:py-32 min-h-[60vh] md:min-h-[80vh] flex items-center justify-center">
                <div className="text-center w-full flex flex-col items-center justify-center">
                    <h1 className="text-[28px] md:text-[44px] font-bold text-black leading-tight max-w-4xl mx-auto">
                        Mounjaro (tirzepatide) works by mimicking two hormones, GLP-1 and GIP, that regulate blood sugar, appetite, and digestion.
                    </h1>
                </div>
            </section>

            {/* Information Grid Section */}
            <section className="animated-section bg-gray-100 px-6 md:px-20 py-12 md:py-16">
                {/* Top Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-24 mb-8 md:mb-16 text-left">
                    <div className="border-t border-gray-300 pt-6">
                        <p className="text-[16px] text-black font-semibold leading-relaxed">
                            This dual-agonist approach helps lower blood glucose levels, reduces appetite, and slows gastric emptying, contributing to weight loss.
                        </p>
                    </div>
                    <div className="border-t border-gray-300 pt-6">
                        <h3 className="text-[16px] font-bold text-black mb-2">Mimicking Gut Hormones</h3>
                        <p className="text-[16px] text-black leading-relaxed">
                            Mounjaro mimics the actions of GLP-1 and GIP, which are naturally produced in the gut and play crucial roles in regulating blood sugar and appetite.
                        </p>
                    </div>
                    <div className="border-t border-gray-300 pt-6">
                        <h3 className="text-[16px] font-bold text-black mb-2">Stimulating Insulin Release</h3>
                        <p className="text-[16px] text-black leading-relaxed">
                            By binding to GIP and GLP-1 receptors, Mounjaro stimulates the pancreas to release more insulin when blood sugar levels rise, helping to control glucose levels.
                        </p>
                    </div>
                </div>

                {/* Bottom Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-24 text-left">
                    <div className="hidden md:block"></div>
                    <div className="border-t border-gray-300 py-6">
                        <h3 className="text-[16px] font-bold text-black mb-2">Reducing Appetite and Satiety</h3>
                        <p className="text-[16px] text-black leading-relaxed">
                            Mounjaro slows down the rate at which food moves through the digestive system, making individuals feel fuller for longer and significantly reducing appetite.
                        </p>
                    </div>
                    <div className="border-t border-gray-300 py-6">
                        <h3 className="text-[16px] font-bold text-black mb-2">Dual-Agonist Advantage</h3>
                        <p className="text-[16px] text-black leading-relaxed">
                            Unlike medications that target only one hormone (like GLP-1), Mounjaro's dual action on both GIP and GLP-1 receptors may lead to more significant weight loss.
                        </p>
                    </div>
                </div>
            </section>

            {/* 3D Model Section - NOW WITH ABOUT PAGE BEHAVIOR */}
            <section
                ref={modelSectionRef}
                className="animated-section px-6 md:px-20 py-10 md:py-6 h-[40vh] md:h-[85vh] relative flex items-center justify-center bg-black"
            >
                <ScrollSeparationModel />
            </section>

            {/* Injection Scroll Stack Section */}
            <ScrollStackSection cards={injectionCards} />

            {/* Why Mounjaro is Good Section */}
            <section className="animated-section bg-gray-100 px-6 md:px-20 py-12 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-1">
                        <h2 className="text-[32px] md:text-[44px] font-bold text-black leading-tight mb-8 md:mb-0">
                            Why is Mounjaro so good?
                        </h2>
                    </div>
                    <div className="md:col-span-2">
                        <div className="space-y-8">
                            {/* Question 1 */}
                            <div className="flex flex-row gap-4 border-b border-gray-200 pb-8 items-start">
                                <div className="text-lg font-bold text-black pt-1">1</div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-left text-black mb-2">What is Mounjaro?</h3>
                                    <p className="text-base text-gray-600 text-left">
                                        Mounjaro (tirzepatide) is a once-weekly injectable prescription medication used to improve blood sugar in adults with type 2 diabetes. It's also being used off-label for weight loss.
                                    </p>
                                </div>
                            </div>
                            {/* Question 2 */}
                            <div className="flex flex-row gap-4 border-b border-gray-200 pb-8 items-start">
                                <div className="text-lg font-bold text-black pt-1">2</div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-left text-black mb-2">
                                        Is Mounjaro FDA approved for weight loss?
                                    </h3>
                                    <p className="text-base text-gray-600 text-left">
                                        As of now, Mounjaro is FDA-approved for type 2 diabetes, not specifically for weight loss. However, a similar medication by the same company, Zepbound (tirzepatide), has been approved for weight loss.
                                    </p>
                                </div>
                            </div>
                            {/* Question 3 */}
                            <div className="flex flex-row gap-4 border-b border-gray-200 pb-8 items-start">
                                <div className="text-lg font-bold text-black pt-1">3</div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-left text-black mb-2">How is Mounjaro taken?</h3>
                                    <p className="text-base text-gray-600 text-left">
                                        It's injected subcutaneously once a week. You can inject it into the thigh, stomach, or upper arm.
                                    </p>
                                </div>
                            </div>
                            {/* Question 4 */}
                            <div className="flex flex-row gap-4 items-start">
                                <div className="text-lg font-bold text-black pt-1">4</div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-left text-black mb-2">How do I store Mounjaro?</h3>
                                    <p className="text-base text-gray-600 text-left">
                                        Store it in the fridge. It can be kept at room temperature (up to 86°F or 30°C) for up to 21 days.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact in Numbers Section */}
            <section className="animated-section bg-gray-100 px-6 md:px-20 py-12 md:py-16">
                <div className="text-center mb-8 md:mb-12">
                    <h2 className="text-3xl md:text-5xl font-bold text-black">Mounjaro has an impact</h2>
                    <h3 className="text-3xl md:text-5xl text-black mt-2">in numbers</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {impactCardsData.map((card, index) => (
                        <ImpactCard key={index} title={card.title} description={card.description} />
                    ))}
                </div>
            </section>

            <TestimonialCarousel />

            {/* Awards Section */}
            <section className="animated-section bg-gray-100 px-6 md:px-20 py-12 md:py-16">
                <div className="text-center mb-8 md:mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold text-black">
                        Industry <span className="font-normal">Recognition</span>
                    </h2>
                </div>
                <div className="space-y-8 text-left">
                    {/* Award 1 */}
                    <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-8 items-start md:items-center border-b border-gray-300 pb-8">
                        <div className="font-semibold text-lg md:text-base">
                            Breakthrough Therapy Designation
                        </div>
                        <div className="text-sm text-black hidden md:block">
                            FDA (U.S. Food and Drug Administration)
                        </div>
                        <div className="text-sm text-black">
                            The FDA granted Mounjaro specific designations highlighting its potential to offer substantial improvement over existing therapies for type 2 diabetes and obesity.
                        </div>
                    </div>
                    {/* Award 2 */}
                    <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-8 items-start md:items-center border-b border-gray-300 pb-8">
                        <div className="font-semibold text-lg md:text-base">
                            NICE Assessment and Endorsement
                        </div>
                        <div className="text-sm text-black hidden md:block">
                            National Institute for Health and Care Excellence (UK)
                        </div>
                        <div className="text-sm text-black">
                            Endorsed for the treatment of type 2 diabetes and obesity management, recognizing its clinical efficacy and cost-effectiveness in the UK healthcare system.
                        </div>
                    </div>
                    {/* Award 3 */}
                    <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-8 items-start md:items-center border-b border-gray-300 pb-8">
                        <div className="font-semibold text-lg md:text-base">Lasker Award for Medical Research</div>
                        <div className="text-sm text-black hidden md:block">Lasker Foundation</div>
                        <div className="text-sm text-black">
                            Researchers received the prestigious Lasker Award for their work on GLP-1-based therapies, which laid the critical scientific foundation for drugs like Mounjaro.
                        </div>
                    </div>
                    {/* Award 4 */}
                    <div className="flex flex-col md:grid md:grid-cols-3 gap-4 md:gap-8 items-start md:items-center border-b border-gray-300 pb-8">
                        <div className="font-semibold text-lg md:text-base">
                            Breakthrough of the Year
                        </div>
                        <div className="text-sm text-black hidden md:block">
                            Science (AAAS)
                        </div>
                        <div className="text-sm text-black">
                             GLP-1 drugs were named Breakthrough of the Year for their transformative impact on obesity treatment and public health.
                        </div>
                    </div>
                </div>
            </section>

            <TrendingSection />
        </div>
    );
}

export default Benefits;